import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetchClient } from "@/lib/api/client";
import { defaultLocale, keys, isSupportedLocale } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { StoreLookupForm } from "@/modules/auth/components/StoreLookupForm";
import {
  AuthBrandPanel,
  AuthMobileHeader,
  BuiltForEgypt,
} from "@/shared/components/auth/AuthBrand";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerPath } from "@/lib/routing/portal-urls";

type SellerSessionPayload = {
  seller?: {
    storeSlug?: string | null;
  };
};

function getResolvedLocale(value: string | undefined) {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

export function LoginPage() {
  const { locale: routeLocale } = useParams();
  const locale = getResolvedLocale(routeLocale);
  const navigate = useNavigate();
  const t = useTranslations();
  const [isCheckingSession, setIsCheckingSession] = useState(true);

  useEffect(() => {
    let isActive = true;

    const resolveAuthenticatedSeller = async () => {
      try {
        const response = await apiFetchClient(endpoints.seller.auth.me, {
          cache: "no-store",
        });

        if (!response.ok) {
          if (isActive) {
            setIsCheckingSession(false);
          }
          return;
        }

        const payload = (await response.json()) as SellerSessionPayload;
        const authenticatedStoreSlug = payload.seller?.storeSlug?.trim();

        if (!isActive) {
          return;
        }

        if (authenticatedStoreSlug) {
          navigate(getSellerPath(locale, authenticatedStoreSlug), { replace: true });
          return;
        }

        setIsCheckingSession(false);
      } catch {
        if (isActive) {
          setIsCheckingSession(false);
        }
      }
    };

    void resolveAuthenticatedSeller();

    return () => {
      isActive = false;
    };
  }, [locale, navigate]);

  return (
    <div className="flex min-h-svh flex-col bg-[color:var(--canvas)] lg:flex-row">
      <AuthBrandPanel
        greeting={t(keys.auth.signinGreeting)}
        title={t(keys.auth.signinWelcomeTitle)}
        subtitle={t(keys.auth.signinWelcomeSubtitle)}
      />

      <section className="flex flex-1 flex-col">
        <AuthMobileHeader
          greeting={t(keys.auth.signinGreeting)}
          title={t(keys.auth.signinWelcomeTitle)}
          tagline={t(keys.invite.heroSubtitleCompact)}
        />

        <div className="flex flex-1 flex-col px-6 pb-7 pt-6 sm:px-10 lg:items-center lg:justify-center lg:px-12 lg:py-12">
          <div className="flex w-full max-w-[440px] flex-1 flex-col gap-6 lg:flex-none lg:gap-7">
            <div className="hidden flex-col gap-2 lg:flex">
              <h2 className="m-0 text-[28px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                {t(keys.auth.signinFormTitle)}
              </h2>
              <p className="m-0 text-[15px] leading-normal text-[color:var(--text-muted)]">
                {t(keys.auth.signinFormSubtitle)}
              </p>
            </div>

            {isCheckingSession ? (
              <div className="flex flex-1 items-center justify-center py-12 text-sm text-[color:var(--text-muted)]">
                {t(keys.common.loading)}
              </div>
            ) : (
              <StoreLookupForm locale={locale} />
            )}

            <span className="text-center text-[13px] text-[color:var(--text-muted)]">
              {t(keys.auth.signinAskInvitePrompt)}{" "}
              <span className="font-bold text-[color:var(--text-primary)] underline underline-offset-2">
                {t(keys.auth.signinAskInviteCta)}
              </span>
            </span>

            <div className="mt-auto lg:hidden">
              <BuiltForEgypt tone="light" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
